import { type ReactNode, Suspense, lazy } from 'react'
import { observer } from 'mobx-react-lite'
import { useFeatures } from '@/contexts/root-store-context'
import {
  AuthenticationContent,
  AuthenticationScreen,
} from '@/features/iam/auth/Authentication'
import { ContentLoading } from '@/components/ContentLoading'
import { ContentError } from '@/components/ContentError'
import { type AuthenticationViewModel } from './AuthenticationViewModel'

const LoginContainer = lazy(() =>
  import('@/features/iam/auth/components/Login/LoginContainer').then((r) => ({
    default: r.LoginContainer,
  })),
)
const WorkspaceSignupContainer = lazy(() =>
  import('@/features/iam/workspaces/WorkspaceSignup/WorkspaceSignupContainer').then(
    (r) => ({
      default: r.WorkspaceSignupContainer,
    }),
  ),
)
const WorkspaceSelectorContainer = lazy(() =>
  import('@/features/iam/workspaces/WorkspaceSelector/WorkspaceSelectorContainer').then(
    (r) => ({
      default: r.WorkspaceSelectorContainer,
    }),
  ),
)

export interface AuthenticationContainerProps {
  vm: AuthenticationViewModel
  children: ReactNode
}

export const AuthenticationContainer = observer(
  function AuthenticationContainer({
    vm,
    children,
  }: AuthenticationContainerProps) {
    const { shellInfo } = useFeatures(({ shell }) => ({
      shellInfo: shell.shellInfo,
    }))

    return (
      <AuthenticationScreen display={vm.display}>
        {vm.fetch.match({
          pending: () => <ContentLoading />,
          rejected: (err) => <ContentError error={err} />,
          resolved: () => (
            <AuthenticationContent
              isLoginVisible={vm.display === 'authentication'}
              landingPageImage={shellInfo.landingPageImageUrl}
              login={
                <Suspense fallback={null}>
                  <LoginContainer vm={vm} />
                </Suspense>
              }
              workspaceSignup={
                <Suspense fallback={null}>
                  <WorkspaceSignupContainer vm={vm.workspaceSignupVm} />
                </Suspense>
              }
              workspaceSelector={
                <Suspense fallback={null}>
                  <WorkspaceSelectorContainer
                    vm={vm.workspaceSelectorVm}
                    workspaceSignupVm={vm.workspaceSignupVm}
                  />
                </Suspense>
              }
            >
              {children}
            </AuthenticationContent>
          ),
        })}
      </AuthenticationScreen>
    )
  },
)
